'use client';

// The admin field error line (BL-2916) and admin's dialect flag. Split out of FormField to keep
// it small. BL-2934: the line is text only, 14/20 · 400 (ADR 030 — Design Canon, amended 2026-11-03).

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Whether the subtree is admin's dialect. Admin turns it on once at its root; bidder-web never
 * sets it, so a shared component reading it keeps bidder's look. Default `false`.
 * `AddressFormFields` reads it for its error line (BL-2916) and its sub-field label (BL-2930).
 */
export const AdminDialectContext = createContext<boolean>(false);

/**
 * Turns on {@link AdminDialectContext} for everything beneath it. A client component, so a
 * server root layout can mount it.
 *
 * @param props - the admin subtree
 * @returns the subtree under the context
 */
export function AdminDialectProvider({ children }: { children: ReactNode }) {
  return <AdminDialectContext.Provider value>{children}</AdminDialectContext.Provider>;
}

/**
 * The admin field error's one style: 14/20 · 400 in `--danger-500`, text only, on the card and
 * on the phone sheet (BL-2934; design owner Vadim Sleva, 2026-11-03: the icon added no
 * information). FormField draws it, and so does every admin error line that can't sit inside a
 * FormField (`FieldError` in admin-frontend). bidder-web keeps its own error line. The card and
 * touch keys are kept on purpose although they now match: PhoneInput's dialect and the sheet
 * tests name them, and the sheet is where a later size split would land.
 */
export const FIELD_ERROR_TEXT = {
  card: 'text-body text-[var(--danger-500)]',
  touch: 'text-body text-[var(--danger-500)]',
} as const;

/**
 * BL-2985 — the one field-hint line (design owner Vadim Sleva, 2026-11-04): 14/20 · 400, 4px under
 * its control, the same size as the label, value and error on desktop and on the phone sheet.
 * `hint` is ink-500 (#6B7280, 4.83:1 on white); `caution` is amber-700 (#B45309, 5.02:1), ADR 034 —
 * Field Editability's warn tier. A Tailwind palette class for the amber, because bidder-web shares
 * this module and defines no admin `--warning-*` var. FormField and admin's `FieldHint` both draw it.
 */
export const FIELD_HINT_CLASS = {
  hint: 'mt-1 text-body text-[var(--ink-500)]',
  caution: 'mt-1 text-body text-amber-700',
} as const;

/** {@link FIELD_ERROR_TEXT} 4px under its control, as FormField places it. */
export const FIELD_ERROR_CLASS = {
  card: `mt-1 ${FIELD_ERROR_TEXT.card}`,
  touch: `mt-1 ${FIELD_ERROR_TEXT.touch}`,
} as const;

/**
 * BL-2916 — a shared widget's field error in the dialect it renders under: admin's form line
 * ({@link FIELD_ERROR_CLASS}, text only since BL-2934) when {@link AdminDialectContext} is on,
 * else the caller's own bidder classes, unchanged (ADR 030 — Design Canon, two dialects).
 *
 * @param props - the error text, whether it sits on a phone sheet, and the bidder classes for the card and touch sizes
 * @returns the error line, or `null` when there is no error
 */
export function DialectFieldError({ message, touch, bidderClassName }: { message?: string; touch: boolean; bidderClassName: { card: string; touch: string } }) {
  const adminDialect = useContext(AdminDialectContext);
  if (!message) return null;
  const size = touch ? 'touch' : 'card';
  if (adminDialect) return <FieldErrorText className={FIELD_ERROR_CLASS[size]}>{message}</FieldErrorText>;
  return <p className={bidderClassName[size]}>{message}</p>;
}

/**
 * A field's error line: the message as text, announced as an alert.
 *
 * @param props - the error line's id and test id (optional), its classes, and its text
 * @returns the error line
 */
export function FieldErrorText({ id, className, testId, children }: { id?: string; className: string; testId?: string; children: ReactNode }) {
  return (
    <p id={id} className={className} role="alert" data-testid={testId}>
      {children}
    </p>
  );
}
